import { useContext } from 'react';
import { KeycloakContext } from './KeycloakProvider';

interface UserInfo {
  sub: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
}

export const useAuth = () => {
  const { keycloak, authenticated, initialized, userRole } = useContext(KeycloakContext);

  const login = () => {
    keycloak?.login();
  };

  const logout = () => {
    keycloak?.logout();
  };

  const getRoles = (): string[] => {
    if (!keycloak?.tokenParsed) return [];
    const tokenParsed = keycloak.tokenParsed as {
      realm_access?: { roles: string[] };
      resource_access?: Record<string, { roles: string[] }>;
    };
    return tokenParsed.realm_access?.roles || [];
  };

  const hasRole = (role: string): boolean => {
    return getRoles().includes(role) || userRole === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    const jwtRoles = getRoles();
    return roles.some((r) => jwtRoles.includes(r)) || (userRole != null && roles.includes(userRole));
  };

  const getToken = (): string | undefined => {
    return keycloak?.token;
  };

  const getUserInfo = (): UserInfo | null => {
    if (!keycloak?.tokenParsed) return null;
    return keycloak.tokenParsed as UserInfo;
  };

  const updateToken = (minValidity = 5): Promise<boolean> => {
    return keycloak?.updateToken(minValidity) || Promise.resolve(false);
  };

  return {
    isAuthenticated: authenticated,
    initialized,
    user: getUserInfo(),
    roles: getRoles(),
    token: getToken(),
    login,
    logout,
    hasRole,
    hasAnyRole,
    userRole,
    updateToken,
    keycloak,
  };
};
